import { useTheme } from '../context/ThemeContext';
import { Background3D } from '../components/Background3D';
import { ArrowUpRight, Github, Atom, Download, Code, BookOpen, AlertCircle, Zap, Database, Terminal } from 'lucide-react';

export function FractalModel() {
  const { theme } = useTheme();

  const technicalSpecs = {
    parameters: "3B (ESM-2 backbone frozen)",
    architecture: "Two-stage: Constraint Predictor + Deterministic Folding",
    maxLength: "1024 residues",
    baseModel: "ESM-2 3B (esm2_t36_3B_UR50D)",
    license: "FOCL",
    trainingData: "~1000 high-resolution PDB structures"
  };

  const capabilities = [
    {
      icon: Atom,
      title: "Distance Prediction",
      description: "64-bin probability distributions over 0-20 Angstroms for inter-residue distances"
    },
    {
      icon: Database,
      title: "Contact Maps",
      description: "Binary contact predictions at 8 Angstrom threshold with high precision"
    },
    {
      icon: Zap,
      title: "Torsion Angles",
      description: "Backbone dihedral angles (phi, psi, omega) with circular loss formulation"
    },
    {
      icon: AlertCircle,
      title: "Confidence Scores",
      description: "Per-residue quality scores (0-100) in pLDDT-style metric"
    }
  ];

  const limitations = [
    "Maximum 1024 residues due to ESM-2 positional encoding constraints",
    "Training on ~1000 structures limits generalization to novel folds",
    "Research/educational tool - not state-of-the-art vs AlphaFold2/3",
    "Monomeric structures only - no multimer prediction",
    "No post-translational modifications or co-factor binding",
    "Folding may converge to local minima for complex topologies"
  ];

  return (
    <div className="relative">
      <Background3D />
      
      {/* Hero Section */}
      <section className="relative pt-24 pb-16">
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-block mb-6">
              <div className={`px-4 py-2 rounded-full text-sm font-medium ${
                theme === 'dark' ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-100 text-primary-700'
              }`}>
                Protein Structure Predictor
              </div>
            </div>
            <h1 className="text-5xl md:text-6xl font-semibold mb-8 leading-tight">
              <span className="text-gradient">FRACTAL</span> 3B
            </h1>
            <p className={`text-xl leading-relaxed max-w-3xl mx-auto mb-8 ${
              theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
            }`}>
              Framework for Representation-guided Atomic ConsTruction & ALignment
            </p>
            <p className={`text-lg leading-relaxed max-w-4xl mx-auto ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
            }`}>
              A constraint-based protein structure prediction system leveraging ESM-2 3B for geometric inference. 
              Two-stage architecture: neural constraint prediction followed by physics-guided deterministic folding.
            </p>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-4 mb-16">
            <a
              href="https://github.com/Aayan-Mishra/FractalGPT"
              target="_blank"
              rel="noopener noreferrer"
              className={`group inline-flex items-center px-6 py-3 rounded-full font-medium transition-all ${
                theme === 'dark'
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-primary-600 text-white hover:bg-primary-700'
              }`}
            >
              <Github className="w-5 h-5 mr-2" />
              View on GitHub
              <ArrowUpRight className="w-4 h-4 ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a
              href="https://github.com/Aayan-Mishra/FractalGPT"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-flex items-center px-6 py-3 rounded-full font-medium border transition-all ${
                theme === 'dark'
                  ? 'border-dark-300 text-gray-300 hover:border-primary-500 hover:text-primary-300'
                  : 'border-gray-300 text-gray-700 hover:border-primary-500 hover:text-primary-600'
              }`}
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Documentation
            </a>
          </div>

          {/* Technical Specs Grid */}
          <div className={`grid md:grid-cols-3 gap-6 p-8 rounded-2xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/50 border border-gray-200'
          }`}>
            {Object.entries(technicalSpecs).map(([key, value], index) => (
              <div key={index} className="text-center">
                <div className={`text-xs font-medium mb-2 uppercase tracking-wider ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </div>
                <div className="text-sm font-semibold">{value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Capabilities */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Key Capabilities
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            {capabilities.map((capability, index) => (
              <div
                key={index}
                className={`p-6 rounded-xl ${
                  theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white/70 border border-gray-200'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${
                    theme === 'dark' ? 'bg-primary-500/20' : 'bg-primary-100'
                  }`}>
                    <capability.icon className="w-6 h-6 text-primary-500" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2">{capability.title}</h3>
                    <p className={`text-sm leading-relaxed ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>
                      {capability.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Architecture */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'}`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Two-Stage Architecture
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  theme === 'dark' ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-100 text-primary-700'
                }`}>
                  1
                </div>
                <h3 className="text-xl font-semibold">Neural Constraint Prediction</h3>
              </div>
              <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                ESM-2 3B (frozen) extracts sequence embeddings, then specialized heads predict:
              </p>
              <ul className={`space-y-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Pairwise distance distributions via outer product attention</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Binary contact maps through convolutional processing</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Backbone torsion angles (phi, psi, omega)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Per-residue confidence scores</span>
                </li>
              </ul>
            </div>

            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white border border-gray-200'
            }`}>
              <div className="flex items-center space-x-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  theme === 'dark' ? 'bg-primary-500/20 text-primary-300' : 'bg-primary-100 text-primary-700'
                }`}>
                  2
                </div>
                <h3 className="text-xl font-semibold">Deterministic Folding</h3>
              </div>
              <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                Physics-guided gradient descent converts constraints to 3D coordinates:
              </p>
              <ul className={`space-y-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Minimizes constraint violation energy</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Gradient-based optimization (1000 steps typical)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>Outputs PDB format structures</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary-500 mr-2">•</span>
                  <span>30-60s folding for 150-300 residue proteins</span>
                </li>
              </ul>
            </div>
          </div>

          <div className={`mt-8 p-6 rounded-xl ${
            theme === 'dark' ? 'bg-primary-500/10 border border-primary-500/30' : 'bg-primary-50 border border-primary-200'
          }`}>
            <p className={`text-sm text-center ${theme === 'dark' ? 'text-primary-300' : 'text-primary-700'}`}>
              <strong>Design Philosophy:</strong> Separating constraint prediction from folding enables rapid experimentation 
              with folding algorithms without retraining neural components.
            </p>
          </div>
        </div>
      </section>

      {/* Installation & Usage */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Installation & Usage
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-4">
                <Download className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold">Installation</h3>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-dark-300' : 'bg-white'
              }`}>
                <div className="mb-2"># Install via pip</div>
                <div className="text-primary-500">pip install fractalml</div>
                <div className="mt-4 mb-2"># Or clone repository</div>
                <div className="text-primary-500">git clone https://github.com/Aayan-Mishra/FractalGPT.git</div>
                <div className="text-primary-500">cd FRACTAL</div>
                <div className="text-primary-500">pip install -e .</div>
              </div>
            </div>

            <div className={`p-6 rounded-xl ${
              theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-gray-50 border border-gray-200'
            }`}>
              <div className="flex items-center space-x-2 mb-4">
                <Terminal className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-semibold">Quick Start</h3>
              </div>
              <div className={`p-4 rounded-lg font-mono text-sm ${
                theme === 'dark' ? 'bg-dark-300' : 'bg-white'
              }`}>
                <div className="mb-2"># WebUI</div>
                <div className="text-primary-500">fractal webui</div>
                <div className="mt-4 mb-2"># CLI</div>
                <div className="text-primary-500">fractal fold input.fasta \</div>
                <div className="text-primary-500 ml-4">--checkpoint Fractal-Labs/FRACTAL-1-3B \</div>
                <div className="text-primary-500 ml-4">--viz</div>
              </div>
            </div>
          </div>

          {/* Python API Example */}
          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-gray-50 border border-gray-200'
          }`}>
            <div className="flex items-center space-x-2 mb-4">
              <Code className="w-5 h-5 text-primary-500" />
              <h3 className="text-lg font-semibold">Python API</h3>
            </div>
            <div className={`p-4 rounded-lg font-mono text-xs overflow-x-auto ${
              theme === 'dark' ? 'bg-dark-300' : 'bg-white'
            }`}>
              <pre className="whitespace-pre-wrap">
{`from fractal.models import ConstraintPredictor
from fractal.geometry.folding import fold_from_constraints

# Initialize pre-trained model
model = ConstraintPredictor.from_pretrained(
    "Fractal-Labs/FRACTAL-1-3B",
    device="cuda"
)

# Predict structure
sequence = "MNIFEMLRIDEGLRLKIYKDTEGYYTIGIGHLLTKSPSLNAA..."
predictions = model.predict_from_sequence(sequence, device="cuda")

# Fold to 3D coordinates
structure = fold_from_constraints(
    predictions,
    num_steps=1000,
    lr=0.01,
    device="cuda"
)

# Export structure
structure.to_pdb("predicted_structure.pdb")`}
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* Performance & Limitations */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'}`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h2 className="text-3xl font-semibold mb-8">
                Performance
              </h2>
              <div className="space-y-4">
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-dark-200/50' : 'bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Constraint Prediction
                    </span>
                    <span className="text-primary-500 font-semibold">2-5s</span>
                  </div>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Typical proteins (150-300 residues) on T4 GPU
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-dark-200/50' : 'bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Deterministic Folding
                    </span>
                    <span className="text-primary-500 font-semibold">30-60s</span>
                  </div>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    Medium-sized proteins (150-300 residues)
                  </p>
                </div>
                <div className={`p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-dark-200/50' : 'bg-white'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Memory Usage
                    </span>
                    <span className="text-primary-500 font-semibold">6-8GB</span>
                  </div>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                    GPU memory for sequences up to 512 residues
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-3xl font-semibold mb-8">
                Limitations
              </h2>
              <div className="space-y-3">
                {limitations.map((limitation, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg flex items-start space-x-3 ${
                      theme === 'dark' ? 'bg-dark-200/50' : 'bg-white'
                    }`}
                  >
                    <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {limitation}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intended Use */}
      <section className="py-20">
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-12 text-center">
            Intended Use & Ethics
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4 text-green-600 dark:text-green-400">
                ✓ Intended Uses
              </h3>
              <ul className="space-y-2">
                {[
                  "Educational demonstrations of structure prediction",
                  "Research prototyping for folding algorithms",
                  "Generating initial structural hypotheses",
                  "Teaching protein bioinformatics",
                  "Structural biology research"
                ].map((use, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <span className="text-green-500 mr-2">•</span>
                    <span>{use}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className={`p-8 rounded-2xl ${
              theme === 'dark' ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
            }`}>
              <h3 className="text-xl font-semibold mb-4 text-red-600 dark:text-red-400">
                ✗ Out-of-Scope Uses
              </h3>
              <ul className="space-y-2">
                {[
                  "Clinical or diagnostic applications",
                  "Drug discovery without validation",
                  "High-stakes production deployments",
                  "Toxin or venom engineering",
                  "Pathogen-related research without oversight"
                ].map((use, index) => (
                  <li key={index} className={`flex items-start ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    <span className="text-red-500 mr-2">•</span>
                    <span>{use}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className={`mt-8 p-6 rounded-xl ${
            theme === 'dark' ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-yellow-50 border border-yellow-200'
          }`}>
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold mb-2 text-yellow-700 dark:text-yellow-400">
                  Ethical Considerations
                </h4>
                <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Protein structure prediction models may be used to design novel proteins with potentially harmful applications. 
                  Users should follow established biosafety guidelines and ethical frameworks when working with predicted structures, 
                  particularly for dual-use biological technologies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Citation */}
      <section className={`py-20 ${theme === 'dark' ? 'bg-dark-200/30' : 'bg-gray-50/50'}`}>
        <div className="max-w-[1440px] mx-auto px-6">
          <h2 className="text-3xl font-semibold mb-8 text-center">
            Citation
          </h2>
          <div className={`p-6 rounded-xl ${
            theme === 'dark' ? 'bg-dark-200/50 border border-dark-300' : 'bg-white border border-gray-200'
          }`}>
            <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
              If you use FRACTAL in your research, please cite both the model and the ESM-2 foundation:
            </p>
            <div className={`p-4 rounded-lg font-mono text-xs overflow-x-auto ${
              theme === 'dark' ? 'bg-dark-300' : 'bg-gray-50'
            }`}>
              <pre className="whitespace-pre-wrap">
{`@software{fractal2025,
  title = {FRACTAL: Framework for Representation-guided Atomic Construction and Alignment},
  author = {Mishra, Aayan},
  year = {2025},
  url = {https://github.com/Aayan-Mishra/FractalGPT}
}

@article{lin2023evolutionary,
  title={Evolutionary-scale prediction of atomic-level protein structure with a language model},
  author={Lin, Zeming and Akin, Halil and Rao, Roshan and Hie, Brian and Zhu, Zhongkai and 
          Lu, Wenting and Smetanin, Nikita and Verkuil, Robert and Kabeli, Ori and 
          Shmueli, Yair and Fazel-Zarandi, Maryam and Sercu, Tom and Candido, Sal and 
          Rives, Alexander},
  journal={Science},
  volume={379},
  number={6637},
  pages={1123--1130},
  year={2023},
  publisher={American Association for the Advancement of Science}
}`}
              </pre>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
              <strong>Developed by:</strong> Aayan Mishra (Huxley Research) • <strong>Version:</strong> 1.0
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
